import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { normalizeStaffFromFirestore, pickPostLoginPath } from '../config/staffFeatures';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const r = searchParams.get('reason');
    if (r === 'disabled') {
      setError('Tu cuenta fue desactivada. Si creés que es un error, contactá al administrador.');
    } else if (r === 'session') {
      setError('Tu sesión expiró o ya no es válida. Iniciá sesión de nuevo.');
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);

      let profile;
      try {
        const getProfile = httpsCallable(functions, 'staffGetOwnProfile');
        const res = await getProfile({});
        profile = res.data;
      } catch (err) {
        await signOut(auth);
        const code = err?.code || '';
        const msg = err?.message || '';
        if (code === 'functions/permission-denied' || code === 'permission-denied') {
          setError('Tu cuenta está desactivada o no tiene acceso.');
        } else if (code === 'functions/not-found') {
          setError('Falta desplegar la función staffGetOwnProfile. En el proyecto: firebase deploy --only functions:staffGetOwnProfile');
        } else if (code === 'functions/unavailable' || code === 'functions/internal') {
          setError('No se pudo cargar tu perfil (servidor). Probá más tarde o verificá que staffGetOwnProfile esté desplegada.');
        } else {
          setError(msg || 'Error al cargar tu perfil. Probá de nuevo.');
        }
        return;
      }

      if (!profile || !profile.exists) {
        await signOut(auth);
        setError('No se encontró el rol del usuario.');
        return;
      }

      const userData = {
        role: profile.role,
        allowedFeatures: profile.allowedFeatures,
        authDisabled: profile.authDisabled
      };
      if (userData.authDisabled === true) {
        await signOut(auth);
        setError('Esta cuenta fue desactivada. Contactá al administrador.');
        return;
      }
      const { role, features } = normalizeStaffFromFirestore(userData);

      if (!(role === 'admin' || role === 'staff' || role === 'viewer')) {
        setError('No tienes permiso para acceder a esta aplicación.');
        return;
      }
      if ((role === 'staff' || role === 'viewer') && features.length === 0) {
        setError('Tu cuenta no tiene permisos asignados. Contactá al administrador.');
        return;
      }

      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', role);
      localStorage.setItem('staffPermissions', JSON.stringify(features));

      if (role === 'admin') {
        localStorage.setItem('isFullAdmin', 'true');
        localStorage.setItem('isAdmin', 'true');
        navigate('/admin');
        window.location.reload();
        return;
      }

      localStorage.setItem('isFullAdmin', 'false');
      localStorage.setItem('isAdmin', 'false');
      const path = pickPostLoginPath(false, features);
      if (path === '/login') {
        setError('No tienes permiso para acceder a esta aplicación.');
        return;
      }
      navigate(path);
      window.location.reload();
    } catch (error) {
      if (error?.code === 'auth/user-disabled') {
        setError('Esta cuenta fue desactivada. Contactá al administrador.');
        return;
      }
      setError('Email o contraseña incorrectos.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" aria-labelledby="login-title">
        <div className="login-brand">
          <span className="login-eyebrow">Panel Privistore</span>
          <h2 id="login-title" className="login-title">Bienvenido de nuevo</h2>
          <p className="login-subtitle">Ingresá con tus credenciales para acceder al panel de gestión.</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
              autoComplete="email"
            />
          </label>

          <label className="login-field">
            <span>Contraseña</span>
            <input
              type="password"
              placeholder="Ingresá tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login-input"
              autoComplete="current-password"
            />
          </label>

          <button type="submit" className="login-button">Iniciar sesión</button>
          {error && <p className="login-error" role="alert">{error}</p>}
        </form>

        <p className="login-access-note">
          Solo para usuarios autorizados. Si querés tu propia tienda, comunicate con el desarrollador a{' '}
          <a href="mailto:info@priball.com">info@priball.com</a>.
        </p>
      </div>
    </div>
  );
};

export default Login;
